import { OrionEclipseAccountDetailModel } from 'src/app/core/clients/generated/client';

/**
 * Whether an account is a candidate for Initial Account Setup rather than a reallocation.
 *
 * **One Eclipse row carrying no model at all.** One row means the account is not sleeved out; no
 * model means nobody has assigned it anything yet. Together that is an untouched account, and
 * setting it up is the request that fits.
 *
 * Anything else is an existing arrangement and wants a Model Allocation / Trade Request instead:
 * more than one row is a sleeved account, and a single row already carrying a model — of any kind
 * — has been set up by somebody.
 *
 * ⚠️ **Custom Holdings counts as a model here**, and that is the point rather than an oversight.
 * This used to read `modelId == null || model === customHoldingsModelName`, treating the
 * hand-traded bucket as "not really a model". But Custom Holdings does not appear by itself —
 * somebody assigned it — so an account carrying it is no longer untouched, and a request against
 * it is a reallocation. Dropping that clause is what routes those accounts to the V2 wizard.
 *
 * ⚠️ Do NOT reach for `isSleeve` to decide sleeved-ness. Eclipse reports `isSleeve: 1` on every
 * row including the bare custodial parent, so it discriminates nothing; row count is the signal.
 *
 * ⚠️ The same `customHoldingsModelName` constant is still used by the wizard's
 * `findPositionSource`, for the unrelated job of locating the hand-traded row to read positions
 * from. That match must keep working — the two uses look alike and are not.
 *
 * A failed lookup must NOT reach this. Absent eligibility and negative eligibility are different
 * states (see `eligibilityUnknown`): recommending on unverified data is worse than not
 * recommending, and withholding a tile on unverified data takes away a valid choice.
 */
export function isInitialAccountEligible(
  rows: OrionEclipseAccountDetailModel[] | undefined,
): boolean {
  if (rows?.length !== 1) return false;
  return rows[0].modelId == null;
}
